import { NavItemModel, NavItemNodeModel, NavNodeModel } from '@libs/shared/models';
import { BehaviorSubject, Observable } from 'rxjs';

export class NavStateService {
  public readonly isActive$: Observable<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  public readonly isExpanded$: Observable<boolean> =
    new BehaviorSubject<boolean>(false);

  public readonly isOpen$: Observable<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  private itemActive?: NavItemNodeModel;
  private itemExpanded?: NavItemNodeModel;

  public constructor(private readonly nav: NavNodeModel) {
    this.initialize();
  }

  public initialize(): void {
    this.initNavTree(this.nav.children);
    this.setActive(this.itemActive);
    this.setExpanded(this.itemExpanded);
  }

  public switchOpen(): void {
    this.nav.expanded = !this.nav.expanded;

    (this.isOpen$ as BehaviorSubject<boolean>).next(this.nav.expanded);
  }

  public collapseItem(): void {
    /* istanbul ignore else */
    if (this.itemExpanded) {
      this.itemExpanded.expanded = false;
      this.updateItemExpanded(this.itemExpanded.parent);
    }
  }

  public switchActive(item: NavItemModel): void {
    this.setActive(item.active ? item.parent : item);
  }

  public switchExpanded(item: NavItemModel): void {
    this.setExpanded(item.expanded || !item.children ? item.parent : item);
  }

  public setActive(item?: NavItemModel): void {
    this.deactivateAll();
    this.activateItem(item);
    this.updateItemActive(item);
  }

  private setExpanded(item?: NavItemModel): void {
    this.collapseAll();
    this.expandItem(item);
    this.updateItemExpanded(item);
  }

  private deactivateAll(): void {
    while (this.itemActive) {
      this.itemActive.active = false;
      this.itemActive = this.itemActive.parent;
    }
  }

  private collapseAll(): void {
    while (this.itemExpanded) {
      this.itemExpanded.expanded = false;
      this.itemExpanded = this.itemExpanded.parent;
    }
  }

  private activateItem(item?: NavItemModel): void {
    let current: NavItemNodeModel | undefined = item;

    while (current) {
      current.active = true;
      current = current.parent;
    }
  }

  private expandItem(item?: NavItemModel): void {
    let current: NavItemNodeModel | undefined = item;

    while (current) {
      current.expanded = true;
      current = current.parent;
    }
  }

  private updateItemActive(item?: NavItemModel): void {
    this.itemActive = item;
    (this.isActive$ as BehaviorSubject<boolean>).next(Boolean(this.itemActive));
  }

  private updateItemExpanded(item?: NavItemModel): void {
    this.itemExpanded = item;
    (this.isExpanded$ as BehaviorSubject<boolean>).next(
      Boolean(this.itemExpanded),
    );
  }

  private initNavTree(items?: readonly NavItemNodeModel[], parent?: NavItemNodeModel): void {
    items?.forEach((item: NavItemNodeModel): void => {
      item.parent = parent;

      if (item.active) {
        this.itemActive = item;
      }

      if (item.expanded) {
        this.itemExpanded = item;
      }

      this.initNavTree(item.children, item);
    });
  }
}
